import React, { useEffect, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import {
	IconBrandTelegram,
	IconBrandVk,
	IconBrandWhatsapp,
	IconChevronDown,
	IconLifebuoy,
	IconMail,
	IconMessageCircle,
	IconPhone,
} from '@tabler/icons-react'
import { useClickOutside } from '@utils/lib/useClickOutside'
import s from './support-button.module.scss'

const channelIcons = {
	telegram: IconBrandTelegram,
	whatsapp: IconBrandWhatsapp,
	vk: IconBrandVk,
	email: IconMail,
	phone: IconPhone,
	chat: IconMessageCircle,
}

export const DEFAULT_SUPPORT_CHANNELS = [
	{
		id: 'telegram-support-bot',
		type: 'telegram',
		label: 'Telegram',
		description: '@sozvuchno_support_bot',
		href: 'https://t.me/sozvuchno_support_bot',
		target: '_blank',
	},{
		id: 'email-support-bot',
		type: 'email',
		label: 'E-mail',
		description: 'support@sozvuchno.ru',
		href: 'mailto:support@sozvuchno.ru',
		target: '_blank',
	},{
		id: 'vk-support-bot',
		type: 'vk',
		label: 'VK',
		description: 'sozvuchno_music',
		href: 'https://vk.ru/sozvuchno_music',
		target: '_blank',
	},
]

const noop = () => {}

const getChannelIcon = (type) => {
	return channelIcons[type] || IconMessageCircle
}

export const SupportButton = ({
	channels = DEFAULT_SUPPORT_CHANNELS,
	label = 'Поддержка',
	ariaLabel = 'Открыть меню поддержки',
	icon,
	theme = 'primary',
	size = 'normal',
	shape = 'rounded',
	placement = 'bottom-end',
	hideChevron = false,
	className,
	buttonClassName,
	menuClassName,
	onOpenChange,
	disabled = false,
	...props
}) => {
	const [isOpen, setOpen] = useState(false)
	const wrapperRef = useRef(null)
	const menuId = useId()
	const Icon = icon || IconLifebuoy

	const closeMenu = () => {
		setOpen(false)
		onOpenChange?.(false)
	}

	useClickOutside(wrapperRef, closeMenu)

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				closeMenu()
			}
		}

		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	})

	const toggleMenu = () => {
		if (disabled) {
			return
		}

		setOpen((prev) => {
			onOpenChange?.(!prev)
			return !prev
		})
	}

	const handleChannelClick = (event, channel) => {
		if (channel.disabled) {
			event.preventDefault()
			return
		}

		channel.onClick?.(event, channel)
		closeMenu()
	}

	return (
		<div className={classNames(s.wrapper, className)} ref={wrapperRef}>
			<button
				type="button"
				className={classNames(
					s.button,
					s[theme],
					s[size],
					s[shape],
					isOpen && s.open,
					buttonClassName
				)}
				onClick={toggleMenu}
				disabled={disabled}
				{...props}
				aria-label={ariaLabel}
				aria-expanded={isOpen}
				aria-controls={menuId}
				aria-haspopup="menu"
			>
				<Icon size={20} stroke={1.8} className={s.buttonIcon} />
				{label ? <span className={s.buttonLabel}>{label}</span> : null}
				{!hideChevron && <IconChevronDown size={18} stroke={1.8} className={s.chevron} />}
			</button>

			<nav
				id={menuId}
				className={classNames(
					s.menu,
					s[placement],
					isOpen && s.menuOpen,
					menuClassName
				)}
				aria-hidden={!isOpen}
			>
				{channels.length > 0 ? (
					<ul className={s.list} role="menu">
						{channels.map((channel) => {
							const ChannelIcon = channel.icon || getChannelIcon(channel.type)
							const itemContent = (
								<>
									<span className={classNames(s.channelIcon, channel.type && s[channel.type])}>
										<ChannelIcon size={20} stroke={1.8} />
									</span>
									<span className={s.channelText}>
										<span className={s.channelLabel}>{channel.label}</span>
										{channel.description ? (
											<span className={s.channelDescription}>{channel.description}</span>
										) : null}
									</span>
								</>
							)

							return (
								<li key={channel.id || channel.href || channel.label} role="none">
									{channel.href ? (
										<a
											className={classNames(s.channel, channel.disabled && s.disabled)}
											href={channel.href}
											target={channel.target}
											rel={channel.rel || (channel.target === '_blank' ? 'noreferrer' : undefined)}
											onClick={(event) => handleChannelClick(event, channel)}
											role="menuitem"
											aria-disabled={channel.disabled}
										>
											{itemContent}
										</a>
									) : (
										<button
											type="button"
											className={classNames(s.channel, channel.disabled && s.disabled)}
											onClick={(event) => handleChannelClick(event, channel)}
											disabled={channel.disabled}
											role="menuitem"
										>
											{itemContent}
										</button>
									)}
								</li>
							)
						})}
					</ul>
				) : (
					<div className={s.empty}>Каналы поддержки не настроены</div>
				)}
			</nav>
		</div>
	)
}

SupportButton.defaultProps = {
	onOpenChange: noop,
}
